/**
 * Offline ticket storage.
 *
 * Non-negotiable 4: the QR ticket renders with no network. Sangam Hall wifi
 * will not survive 300 people opening the app at 6:55pm, so the ticket is
 * written to the device the moment it is issued and read from there first —
 * the network is the fallback, not the source.
 *
 * `localStorage`, not IndexedDB: a ticket is a few hundred bytes, a member
 * holds a handful, and synchronous reads mean the QR paints on the first
 * frame instead of after a promise resolves. IndexedDB would be the right
 * answer for the door scanner's guest list, which is a different size of
 * problem.
 */
const KEY = "sangam.tickets";

export interface StoredTicket {
  ref: string;
  state: string;
  qr_payload: string;
  seats: string[];
  total_idr: number;
  program: {
    id: number;
    slug: string;
    date_time: string;
    venue: string;
    name: string;
  };
  saved_at: string;
}

function readAll(): Record<string, StoredTicket> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, StoredTicket>;
  } catch {
    // Corrupt or unavailable storage must not blank the screen; the member
    // falls back to the network copy.
    return {};
  }
}

function writeAll(all: Record<string, StoredTicket>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* quota or private mode — the ticket still works while online */
  }
}

export function saveTicket(ticket: Omit<StoredTicket, "saved_at">): void {
  // Only a paid ticket is worth keeping. A HELD booking saved offline would
  // show a member a QR for seats they no longer have.
  if (!ticket.qr_payload) return;
  const all = readAll();
  all[ticket.ref] = { ...ticket, saved_at: new Date().toISOString() };
  writeAll(all);
}

export function loadTicket(ref: string): StoredTicket | null {
  return readAll()[ref] ?? null;
}

export function loadTickets(): StoredTicket[] {
  return Object.values(readAll()).sort((a, b) =>
    a.program.date_time.localeCompare(b.program.date_time),
  );
}

/** Called after a refund or cancellation, so a dead ticket does not sit on
 *  the device looking scannable. */
export function forgetTicket(ref: string): void {
  const all = readAll();
  delete all[ref];
  writeAll(all);
}
